/**
 * Product Showcase – center carousel with 25% side peek
 */
(function () {
  'use strict';

  function debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), delay);
    };
  }

  function getTranslateX(el) {
    const transform = window.getComputedStyle(el).transform;
    if (!transform || transform === 'none') return 0;
    return new DOMMatrix(transform).m41;
  }

  function waitForImages(container) {
    const images = container.querySelectorAll('img');
    if (!images.length) return Promise.resolve();

    const waits = Array.from(images).map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) resolve();
          else {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          }
        })
    );

    return Promise.race([Promise.all(waits), new Promise((r) => setTimeout(r, 1500))]);
  }

  function initShowcase(root) {
    const track = root.querySelector('[data-ps-track]');
    const viewport = root.querySelector('[data-ps-viewport]');
    const prevBtn = root.querySelector('[data-ps-prev]');
    const nextBtn = root.querySelector('[data-ps-next]');
    const detailsPanels = root.querySelectorAll('[data-ps-details]');
    const enableLoop = root.dataset.loop !== 'false';

    if (!track || !viewport) return;

    let originalCount = 0;
    let realIndex = 0;
    let trackIndex = 0;
    let isAnimating = false;
    let animTimer = null;
    let isFullLayout = false;

    function getOriginalSlides() {
      return Array.from(track.querySelectorAll('.ps-slide:not(.ps-slide--clone)'));
    }

    function getAllSlides() {
      return Array.from(track.querySelectorAll('.ps-slide'));
    }

    function clearAnimLock() {
      isAnimating = false;
      if (animTimer) {
        clearTimeout(animTimer);
        animTimer = null;
      }
    }

    function lockAnim() {
      isAnimating = true;
      if (animTimer) clearTimeout(animTimer);
      animTimer = setTimeout(clearAnimLock, 550);
    }

    function clearClones() {
      track.querySelectorAll('.ps-slide--clone').forEach((el) => el.remove());
    }

    function updateDetails(index) {
      detailsPanels.forEach((panel) => {
        const panelIndex = parseInt(panel.getAttribute('data-index'), 10);
        const active = panelIndex === index;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
    }

    function setActiveSlide(slide) {
      getAllSlides().forEach((s) => {
        s.classList.toggle('is-active', s === slide);
      });

      if (slide) {
        const idx = parseInt(slide.getAttribute('data-index'), 10);
        updateDetails(idx);
        realIndex = idx;
      }
    }

    function alignSlideToCenter(slide, animate) {
      if (!slide) return;

      const slideRect = slide.getBoundingClientRect();
      const viewRect = viewport.getBoundingClientRect();
      const slideCenterX = slideRect.left + slideRect.width / 2;
      const viewCenterX = viewRect.left + viewRect.width / 2;
      const delta = slideCenterX - viewCenterX;
      const nextX = getTranslateX(track) - delta;

      track.classList.toggle('is-resetting', !animate);
      track.style.transform = `translate3d(${nextX}px, 0, 0)`;
    }

    function getSlideByTrackIndex(index) {
      return getAllSlides()[index] || null;
    }

    function goToTrackIndex(index, animate) {
      const slide = getSlideByTrackIndex(index);
      if (!slide) return;

      trackIndex = index;
      setActiveSlide(slide);
      alignSlideToCenter(slide, animate);
    }

    function goToRealIndex(index, animate) {
      const originals = getOriginalSlides();
      const slide = originals[index];
      if (!slide) return;

      trackIndex = index;
      setActiveSlide(slide);
      alignSlideToCenter(slide, animate);
    }

    function updateNav() {
      if (enableLoop && originalCount >= 2) {
        if (prevBtn) prevBtn.disabled = false;
        if (nextBtn) nextBtn.disabled = false;
        return;
      }

      if (prevBtn) prevBtn.disabled = realIndex <= 0;
      if (nextBtn) nextBtn.disabled = realIndex >= originalCount - 1;
    }

    function buildClones() {
      clearClones();

      if (!enableLoop || originalCount < 2) return;

      getOriginalSlides().forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.classList.remove('is-active');
        clone.classList.add('ps-slide--clone');
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });
    }

    function snapLoopIfNeeded() {
      if (!enableLoop || originalCount < 2 || trackIndex < originalCount) return;

      goToTrackIndex(trackIndex - originalCount, false);
    }

    function realign(animate) {
      if (isFullLayout) return;

      if (!enableLoop || originalCount < 2) {
        goToRealIndex(realIndex, animate);
      } else {
        goToTrackIndex(trackIndex, animate);
      }
    }

    function onTransitionEnd(event) {
      if (event.target !== track || event.propertyName !== 'transform') return;
      clearAnimLock();
      snapLoopIfNeeded();
    }

    function move(direction) {
      if (isAnimating || originalCount < 1) return;

      if (!enableLoop || originalCount < 2) {
        const next = realIndex + direction;
        if (next < 0 || next >= originalCount) return;
        lockAnim();
        goToRealIndex(next, true);
        updateNav();
        return;
      }

      if (direction === 1 && trackIndex === originalCount - 1) {
        lockAnim();
        goToTrackIndex(originalCount, true);
        updateNav();
        return;
      }

      if (direction === -1 && trackIndex === 0) {
        goToTrackIndex(originalCount, false);
        requestAnimationFrame(() => {
          lockAnim();
          goToTrackIndex(originalCount - 1, true);
          updateNav();
        });
        return;
      }

      lockAnim();
      goToTrackIndex(trackIndex + direction, true);
      updateNav();
    }

    async function fullLayout() {
      isFullLayout = true;
      root.removeAttribute('data-ps-layout-ready');

      track.classList.add('is-resetting');
      track.style.transform = 'translate3d(0, 0, 0)';
      clearClones();
      getAllSlides().forEach((s) => s.classList.remove('is-active'));

      originalCount = getOriginalSlides().length;
      root.dataset.single = originalCount <= 1 ? 'true' : 'false';
      realIndex = Math.min(realIndex, Math.max(0, originalCount - 1));
      trackIndex = realIndex;

      buildClones();
      await waitForImages(root);

      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));

      if (!enableLoop || originalCount < 2) {
        goToRealIndex(realIndex, false);
      } else {
        goToTrackIndex(realIndex, false);
      }

      updateNav();
      track.classList.remove('is-resetting');
      root.setAttribute('data-ps-layout-ready', 'true');
      isFullLayout = false;
    }

    track.addEventListener('transitionend', onTransitionEnd);
    if (prevBtn) prevBtn.addEventListener('click', () => move(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => move(1));

    window.addEventListener('resize', debounce(() => realign(false), 200));

    fullLayout();
  }

  function initAll() {
    document.querySelectorAll('[data-product-showcase]').forEach((root) => {
      if (root.dataset.psInit === 'true') return;
      root.dataset.psInit = 'true';
      initShowcase(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-product-showcase]');
    if (!root) return;
    root.dataset.psInit = 'false';
    root.removeAttribute('data-ps-layout-ready');
    initShowcase(root);
  });
})();
